import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, PropsWithChildren } from 'react';
import { QueryKey } from '@/Config/api/queryKey';
import { getScenarioId } from '@/Config/auth/authStore';
import { Spinner } from '@/Domain/components/Shared/Spinner';

export interface BootstrapConfig {
  apiServerUrl: string;
  appServerUrl: string;
  apiKey: string;
  humanHandoffActive: boolean;
  previewIdentityToken: string;
}

interface BootstrapConfigResponse {
  api_server_url: string;
  app_server_url: string;
  api_key: string;
  human_handoff_active: boolean;
  preview_identity_token: string;
}

interface BootstrapResponse {
  config: BootstrapConfigResponse;
  integrations: unknown;
  account: unknown;
  implementation: unknown;
  usage: unknown;
  implementations: string[];
}

const BOOTSTRAP_CONFIG_DEFAULT: BootstrapConfig = {
  apiServerUrl: '',
  appServerUrl: '',
  apiKey: '',
  humanHandoffActive: false,
  previewIdentityToken: '',
};

const BootstrapConfigContext = createContext<BootstrapConfig | null>(null);

/**
 * Returns the bootstrap config provided by {@link BootstrapProvider}. Outside
 * of a provider (e.g. in isolated unit tests that render a leaf component) it
 * returns an empty-string default so components that read `apiServerUrl` or
 * `apiKey` render without crashing. In production every consumer is a
 * descendant of `AuthGate`, which always renders a provider.
 */
export const useBootstrapConfig = (): BootstrapConfig =>
  useContext(BootstrapConfigContext) ?? BOOTSTRAP_CONFIG_DEFAULT;

interface BootstrapProviderProps extends PropsWithChildren {
  onUnauthenticated: () => void;
}

export function BootstrapProvider({
  children,
  onUnauthenticated,
}: BootstrapProviderProps): JSX.Element {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<BootstrapConfig | null>(null);

  const scenarioId = getScenarioId();

  // Don't fire bootstrap at all when there's no scenario_id in the URL.
  // We end up here for users without any chat implementation yet (e.g.
  // brand-new social-auth signups landing at `/`, or invited users on
  // `/i/accept-invitation`). The endpoint requires scenario-id as a header
  // and would 422; we'd show a "failed to load" screen the user can't
  // escape from. With `enabled: false` the children render with the
  // BOOTSTRAP_CONFIG_DEFAULT context, which is enough for the entry/non-
  // operator routes that own these paths.
  const { data, isLoading, isError, error } = useQuery<BootstrapResponse>({
    queryKey: [QueryKey.app.bootstrap, scenarioId],
    retry: false,
    staleTime: Infinity,
    enabled: scenarioId !== '',
  });

  useEffect(() => {
    if (isError && error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 401) {
        onUnauthenticated();
      }
    }
  }, [isError, error, onUnauthenticated]);

  useEffect(() => {
    if (data) {
      queryClient.setQueryData(['integrations'], data.integrations);
      queryClient.setQueryData(['account'], data.account);
      queryClient.setQueryData(['implementation'], data.implementation);
      queryClient.setQueryData([QueryKey.subscription.usage], data.usage);
      queryClient.setQueryData(['implementations'], data.implementations);

      setConfig({
        apiServerUrl: data.config.api_server_url,
        appServerUrl: data.config.app_server_url,
        apiKey: data.config.api_key,
        humanHandoffActive: data.config.human_handoff_active,
        previewIdentityToken: data.config.preview_identity_token,
      });
    }
  }, [data, queryClient]);

  // No scenario in URL → bootstrap is disabled; just hand the children the
  // default context so routes like AcceptInvitationPage can render.
  if (scenarioId === '') {
    return (
      <BootstrapConfigContext.Provider value={BOOTSTRAP_CONFIG_DEFAULT}>
        {children}
      </BootstrapConfigContext.Provider>
    );
  }

  if (isLoading) {
    return (
      <div
        data-testid="bootstrap-loading"
        className="flex items-center justify-center h-screen w-screen"
      >
        <Spinner className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  if (isError && !config) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 401) {
      return (
        <div
          data-testid="bootstrap-error"
          className="flex flex-col items-center justify-center h-screen w-screen gap-4"
        >
          <p className="text-muted-foreground">Failed to load application.</p>
          <button className="text-sm text-info underline" onClick={() => window.location.reload()}>
            Refresh page
          </button>
        </div>
      );
    }
  }

  if (!config) {
    return (
      <div
        data-testid="bootstrap-loading"
        className="flex items-center justify-center h-screen w-screen"
      >
        <Spinner className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  return (
    <BootstrapConfigContext.Provider value={config}>{children}</BootstrapConfigContext.Provider>
  );
}
